import type { RefObject } from 'react';
import { Smile } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover } from './Popover';
import { PickerBody } from './CustomEmojiPicker';

/**
 * Smiley button for message text fields: pick a custom emoji, its chat code
 * (`<:name:id>`) is inserted at the cursor. Goes through the native value
 * setter + an `input` event so controlled React fields see the change.
 */
export function EmojiInsertButton({
  target,
  className,
  disabled,
}: {
  target: RefObject<HTMLTextAreaElement | HTMLInputElement | null>;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <Popover
      width={384}
      align="right"
      trigger={({ onClick, ref }) => (
        <div ref={ref} className={className}>
          <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title="Custom Emoji einfügen"
            aria-label="Custom Emoji einfügen"
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors',
              'hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50'
            )}
          >
            <Smile className="h-4 w-4" />
          </button>
        </div>
      )}
      content={(close) => (
        <PickerBody
          allowUnicode={false}
          format="chat"
          onPick={(code) => {
            if (target.current) insertAtCursor(target.current, code);
            close();
          }}
        />
      )}
    />
  );
}

function insertAtCursor(el: HTMLTextAreaElement | HTMLInputElement, text: string) {
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const next = el.value.slice(0, start) + text + el.value.slice(end);
  if (el.maxLength > 0 && next.length > el.maxLength) return;
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, next);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.focus();
  const caret = start + text.length;
  el.setSelectionRange(caret, caret);
}
